import {
  CITY_NAMES, DEMAND_SPREAD, DEMAND_START_FRACTION, DEMAND_SUPPLY_RATIO,
  DRAIN_MARGIN, EXTENSION_CONFIG, GOOD_IDS, GOOD_IDS_ALL, MILESTONES,
  PRODUCER_CAP,
  PRODUCER_NAMES, PRODUCER_RATE, RATE_JITTER, RECIPE, REFINERY_RATE,
  START_CONFIG, START_MONEY,
  START_STOCK_SECONDS, TRANSFER_CAP, TRANSFER_NAMES, TWO_GOOD_CITIES,
  WORLD_H, WORLD_W, REGION_STREAM, REGION_W, MAX_REGIONS,
} from "./constants";
import { distance, drainable } from "./haul";
import { between, rng, shuffled } from "./rng";
import type { Rng } from "./rng";
import type {
  CityNode, GameNode, GameState, GoodId, MilestoneId, ProducedGoodId, ProducerNode,
  TransferNode, WorldConfig, WorldRect,
} from "./types";

/**
 * Map generation, RFC-0005.
 *
 * The whole of the game's randomness lives in this file and in rng.ts. It runs
 * once per world and once per milestone, always before the next step().
 *
 * Two rules do the heavy lifting and neither of them retries:
 *
 * - a producer's rate is capped by what its best single route could carry, so a
 *   map can never contain a producer nobody can empty;
 * - demand is *allocated out of* the production that was just generated, so
 *   peak demand is DEMAND_SUPPLY_RATIO × supply by construction rather than by
 *   luck. That is the invariant CLAUDE.md calls the most important one.
 *
 * Positions are the only thing sampled and rejected, and the seed sweep in
 * test/worldgen.test.ts is what proves the rejection never runs out of tries.
 */

/**
 * Nodes are readonly to everyone else on purpose — a map does not move. Inside
 * the generator, position, rate and demand are decided in three passes, because
 * each one depends on the pass before it.
 */
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

const nearest = (p: { x: number; y: number }, list: readonly GameNode[]): number =>
  list.length ? Math.min(...list.map((n) => distance(n, p))) : Infinity;

/** A full per-good record at zero. Never a partial one — see CityNode.demand. */
const emptyGoods = (): Record<GoodId, number> =>
  Object.fromEntries(GOOD_IDS_ALL.map((g) => [g, 0])) as Record<GoodId, number>;

/**
 * The goods a city wants. An appetite is a positive ceiling, and that is the
 * whole definition — there is no separate list to keep in sync.
 *
 * Exported from here rather than from sim.ts because the generator needs it
 * while assigning, and sim.ts already imports this module. The panel and the
 * renderer ask the same question the player is looking at.
 */
export const wantedGoods = (c: CityNode): GoodId[] =>
  GOOD_IDS_ALL.filter((g) => c.demandMax[g] > 0);

/** Player-facing name, skipping what the world already carries. */
function nameFrom(pool: readonly string[], taken: Set<string>, r: Rng): string {
  for (const candidate of shuffled(r, pool)) {
    if (!taken.has(candidate)) return candidate;
  }
  // Only reachable past the last milestone; a counter beats a collision.
  for (let n = 2; ; n++) {
    const candidate = `${pool[0]} ${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

interface Spot { x: number; y: number }

/**
 * A position at least `minDistance` from everything already placed.
 *
 * With no bias the first valid candidate wins and the loop stops — at these
 * densities that is almost always the first draw. With a bias every draw is
 * made and scored, because the brief is about which of the valid spots is
 * *best*, not whether one exists.
 *
 * When the draws come up empty the band is swept on a grid. That is not a
 * relaxation — `minDistance` is unchanged, and so is the brief — it only stops
 * the last milestone of an unlucky world from throwing when there was room all
 * along. Measured before it existed: 5 worlds in 5000 died on the final stage
 * with 500 draws, which is a blank map for one player in a thousand. The sweep
 * consumes no randomness, so a world's stream is the same whether it runs or
 * not.
 *
 * Returns null when even the sweep finds nothing: then the board really is
 * full. What the caller does with that depends on whether it is building a
 * world or growing one — see generateNodes().
 */
function place(
  r: Rng,
  cfg: WorldConfig,
  taken: readonly GameNode[],
  band: [number, number],
  score: ((spot: Spot) => number) | null, rect: WorldRect,
): Spot | null {
  const fits = (spot: Spot): boolean =>
    taken.every((n) => distance(n, spot) >= cfg.minDistance);

  let best: Spot | null = null;
  let bestScore = Infinity;
  const consider = (spot: Spot): boolean => {
    if (!fits(spot)) return false;
    if (!score) {
      best = spot;
      return true;
    }
    const value = score(spot);
    if (value < bestScore) {
      bestScore = value;
      best = spot;
    }
    return false;
  };

  for (let i = 0; i < cfg.tries; i++) {
    const spot = {
      x: Math.round(between(r, band[0], band[1])),
      y: Math.round(between(r, rect.y + cfg.margin, rect.y + rect.height - cfg.margin)),
    };
    if (consider(spot)) return spot;
  }
  if (best) return best;

  const step = cfg.minDistance / 3;
  for (let x = band[0]; x <= band[1]; x += step) {
    for (let y = rect.y + cfg.margin; y <= rect.y + rect.height - cfg.margin; y += step) {
      const spot = { x: Math.round(x), y: Math.round(y) };
      if (consider(spot)) return spot;
    }
  }
  if (best) return best;

  return null;
}

/**
 * The nodes for a map, or the nodes to ADD to one.
 *
 * Returns only the new nodes; `existing` is read and never modified, so a caller
 * appends: `s.nodes.push(...generateNodes(seed, cfg, s.nodes))`. Ids continue
 * from the highest id in `existing`, or start at 0.
 *
 * **A growth step places what fits.** The board has a finite capacity, and a
 * late milestone on an unlucky seed can run out of room for its last node —
 * measured once in 30 000 worlds. A milestone that hands out five nodes instead
 * of six is invisible; a milestone that throws is a dead game on a map the
 * player had been enjoying. Every invariant still holds on the smaller list,
 * because demand is allocated out of the production that was actually placed.
 *
 * @throws only when a *fresh* map cannot be built at all (`existing` empty and
 *   nothing placed). That is a broken config rather than an unlucky seed, and it
 *   should be loud. The seed sweep in test/worldgen.test.ts is what keeps it
 *   unreachable for the shipped ones.
 */
export function generateNodes(
  seed: number,
  config: WorldConfig = START_CONFIG,
  existing: readonly GameNode[] = [],
  rect: WorldRect = { x: 0, y: 0, width: WORLD_W, height: WORLD_H },
): GameNode[] {
  const r = rng(seed);
  const bandLo = rect.x + rect.width * (0.5 - config.cityBand / 2);
  const bandHi = rect.x + rect.width * (0.5 + config.cityBand / 2);
  const outerLeft: [number, number] = [rect.x + config.margin, rect.x + rect.width * config.producerBand];
  const outerRight: [number, number] = [rect.x + rect.width * (1 - config.producerBand), rect.x + rect.width - config.margin];

  const board: GameNode[] = [...existing];
  const added: GameNode[] = [];
  const names = new Set(existing.map((n) => n.name));
  const firstId = existing.reduce((max, n) => Math.max(max, n.id + 1), 0);
  let producerIndex = existing.filter((n) => n.kind === "producer").length;

  /**
   * Ids in a canonical order — producers, then cities, then hubs — whatever
   * order placement ran in. Placement puts cities down first, because both
   * briefs measure a producer against the cities; ids are what a save file and
   * a bug report read, and there they should be sorted by kind.
   */
  const finish = (nodes: GameNode[]): GameNode[] => {
    const order = { producer: 0, city: 1, transfer: 2 };
    const out = [...nodes].sort((a, b) => order[a.kind] - order[b.kind]);
    out.forEach((n, i) => { (n as Mutable<GameNode>).id = firstId + i; });
    if (existing.length === 0 && out.length < config.producers + config.cities) {
      throw new Error("Startkarte lässt sich mit dieser Konfiguration nicht bauen");
    }
    return out;
  };

  const push = (node: GameNode): void => {
    board.push(node);
    added.push(node);
    names.add(node.name);
  };
  const cities = (): CityNode[] => board.filter((n): n is CityNode => n.kind === "city");
  const producers = (): ProducerNode[] =>
    board.filter((n): n is ProducerNode => n.kind === "producer");

  const placeCities = (): void => {
    for (let i = 0; i < config.cities; i++) {
      const spot = place(r, config, board, [bandLo, bandHi], null, rect);
      if (!spot) break;
      push({
        kind: "city",
        id: 0, // canonical ids are handed out at the end of this function

        name: nameFrom(CITY_NAMES, names, r),
        x: spot.x,
        y: spot.y,
        // Which goods this city wants is decided below, once every producer of
        // this step is on the board; how much, once the rates are known.
        demand: emptyGoods(),
        demandMax: emptyGoods(),
        backlog: emptyGoods(),
        trust: 1,
      });
    }
  };

  const placeProducers = (): void => {
    // Producers after the cities, because both briefs are about where a producer
    // sits relative to them — and a brief measured against half the cities is a
    // brief that misses: the umschlag step used to put its "far" producer 175 px
    // from a city it placed two draws later.
    for (let i = 0; i < config.producers; i++) {
      const good = config.good ?? GOOD_IDS[producerIndex % GOOD_IDS.length]!;
      const band = producerIndex % 2 === 0 ? outerLeft : outerRight;
      const known = cities();
      let score: ((spot: Spot) => number) | null = null;
      if (known.length > 0 && config.bias === "tight") {
        const target = config.tightTarget ?? 0;
        score = (spot) => Math.abs(nearest(spot, known) - target);
      } else if (known.length > 0 && config.bias === "wide") {
        // Negated because place() keeps the lowest score.
        score = (spot) => -nearest(spot, known);
      }
      const spot = place(r, config, board, band, score, rect);
      if (!spot) break;
      push({
        kind: "producer",
        id: 0,

        name: nameFrom(PRODUCER_NAMES[good], names, r),
        x: spot.x,
        y: spot.y,
        good,
        // Filled in below, once every producer of this step is placed: the rate
        // depends on the distance, and the cap on it is arithmetic, not a draw.
        rate: 0,
        cap: PRODUCER_CAP,
        level: 0,
        stock: 0,
      });
      producerIndex++;
    }
  };

  // Order matters twice. A brief measures a producer against the cities, so
  // when there is one the cities go down first — otherwise "as far from every
  // city as possible" is measured against half of them. With no brief the
  // producers go first, because their two strips are the tighter space and the
  // last milestone is where the board runs out of room.
  if (config.bias === "none") {
    placeProducers();
    placeCities();
  } else {
    placeCities();
    placeProducers();
  }

  for (let i = 0; i < config.transfers; i++) {
    // A hub earns its extra hop only between the goods and the mouths.
    const p = producers();
    const c = cities();
    const score = p.length && c.length
      ? (spot: Spot) => nearest(spot, p) + nearest(spot, c)
      : null;
    const spot = place(r, config, board, [rect.x + config.margin, rect.x + rect.width - config.margin], score, rect);
    if (!spot) break;
    push({
      kind: "transfer",
      id: 0,

      name: nameFrom(TRANSFER_NAMES, names, r),
      x: spot.x,
      y: spot.y,
      cap: TRANSFER_CAP,
      stock: emptyGoods(),
      // Corrected while building phase 2: this used to be flipped afterwards, in
      // extensionFor(). It cannot be — the werkzeug budget below is computed from
      // the refineries on the board, and that runs first (RFC-0015 §3.4).
      refines: config.refines ?? false,
    });
  }

  const allCities = cities();
  const fresh = added.filter((n): n is CityNode => n.kind === "city");

  // ── Which goods, before how much (RFC-0016 §3.4) ─────────────────────────
  //
  // The drain cap has to measure against the nearest city that wants this
  // producer's good, and the demand is allotted out of the capped rates. Only
  // circular while assignment and magnitude are one step — they are not.
  //
  // A fresh city takes from the goods *this step* added. Nothing from an earlier
  // stage: a step that adds an erz producer and a holz city would otherwise grow
  // the erz budget with nobody to spend it on, and hand its new city a share of
  // a holz budget the earlier stages already allotted — a city with no appetite
  // on a map with unallocated production.
  const freshGoods = [...new Set(
    added.filter((n): n is ProducerNode => n.kind === "producer").map((p) => p.good),
  )];
  const menu = freshGoods.length > 0 ? freshGoods : [...GOOD_IDS];
  const olderCities = allCities.length - fresh.length;
  const wanted = new Map<CityNode, GoodId[]>(allCities.map((c) => [c, wantedGoods(c)]));
  const reach = (c: CityNode, g: GoodId): number =>
    nearest(c, producers().filter((p) => p.good === g));

  // **A city wants what grows next to it.** Measured against the alternative on
  // seeds 1..3: handing the goods out round-robin leaves five of nine cities
  // permanently at TRUST_MIN, because the towns 100 px from their sawmill win
  // every race for its stock against the towns 400 px away — mean trust 0.72
  // against 0.89 here, and the map was never the problem (every city can be
  // reached 2.8× over). RFC-0016 §9.2: the pairing belongs in the assignment,
  // where it costs one comparison, not in the placement, where it would change
  // what a map is.
  const picks = fresh.map((c) =>
    menu.reduce((best, g) => (reach(c, g) < reach(c, best) ? g : best), menu[0]!));

  // Every good this step produces needs a taker, or its budget goes unspent and
  // its producers have nobody who pays full price. The city handed over is the
  // one closest to the orphaned good, and only ever one that leaves a taker
  // behind — swapping the last taker of one good for another trades the problem
  // rather than fixing it. With the shipped configs a step never has more goods
  // than cities, so the loop below finds a victim whenever it needs one.
  for (const g of menu) {
    if (picks.includes(g)) continue;
    let victim = -1;
    for (let i = 0; i < fresh.length; i++) {
      if (picks.filter((p) => p === picks[i]).length < 2) continue;
      if (victim < 0 || reach(fresh[i]!, g) < reach(fresh[victim]!, g)) victim = i;
    }
    if (victim >= 0) picks[victim] = g;
  }

  fresh.forEach((c, i) => {
    const first = picks[i]!;
    const goods = [first];
    // The second good goes to the earliest cities on the map, which are the
    // start map's — a later stage's budget for the other good is long since
    // allotted, so a second appetite there would be a zero entry pretending to
    // be an appetite.
    if (olderCities + i < TWO_GOOD_CITIES) {
      // Rotated over what producers make, not over every good there is: an
      // appetite for werkzeug on a map with no refinery is a promise no route
      // can answer (RFC-0015 §3.6).
      const next = GOOD_IDS[(GOOD_IDS.indexOf(first) + 1) % GOOD_IDS.length]!;
      if (next !== first) goods.push(next);
    }
    wanted.set(c, goods);
  });

  // Rates: base, jittered, then capped by what this producer's best road to a
  // city that actually wants its good could ever move.
  for (const p of added) {
    if (p.kind !== "producer") continue;
    const jittered = PRODUCER_RATE[p.good] * (1 + between(r, -RATE_JITTER, RATE_JITTER));
    // No taker means no map to measure against; fall back to the whole list
    // rather than to Infinity. Unreachable with the shipped configs, because
    // `menu` gives every good this step produces a fresh city.
    const takers = allCities.filter((c) => wanted.get(c)!.includes(p.good));
    const ceiling = drainable(nearest(p, takers.length ? takers : allCities)) * DRAIN_MARGIN;
    const rate = Math.round(Math.min(jittered, ceiling) * 100) / 100;
    const mutable = p as Mutable<ProducerNode>;
    mutable.rate = rate;
    mutable.stock = Math.round(rate * START_STOCK_SECONDS);
  }

  // Demand out of the budget that production just created — one budget per good.
  // Nothing to retry: no good's demand can come out above DEMAND_SUPPLY_RATIO ×
  // its own supply, which is the invariant per good rather than in total.
  if (fresh.length > 0) {
    const budgets = new Map<GoodId, number>();
    for (const g of GOOD_IDS_ALL) {
      const supply = producers().reduce((s, n) => s + (n.good === g ? n.rate : 0), 0);
      const taken = allCities
        .filter((c) => !fresh.includes(c))
        .reduce((s, c) => s + c.demandMax[g], 0);
      budgets.set(g, supply * DEMAND_SUPPLY_RATIO - taken);
    }
    // Werkzeug, before anything is split (RFC-0015 §1.2). A refinery is its
    // producer and REFINERY_RATE is its rate, so the budget has the shape every
    // other good's has — it is only paid for twice over, out of both inputs,
    // because that is what the recipe consumes. Which is also the safety
    // argument: two units of raw demand become one of refined, so this can only
    // move a map away from the supply limit.
    //
    // The mouth is a *fresh* city, not simply the nearest one on the board.
    // RFC-0016 §3.5: a growth step may not change what an existing city wants,
    // and reaching into an older record from here would do exactly that — the
    // sweep caught it as werkzeug demand appearing on stages that have no
    // refinery, because the earlier stages hold the same city objects.
    // Handed over directly rather than through the split below, which divides
    // one budget among several eaters; werkzeug has one.
    const refineries = board.filter(
      (n): n is TransferNode => n.kind === "transfer" && n.refines);
    if (refineries.length > 0) {
      // The ratio sits outside the min, not on the first term. A refinery's rate
      // is a *throughput* number — it has to be able to swallow what a road
      // delivers, or the shared store fills with one input and the other can
      // never unload (RFC-0015 §7). Sizing the appetite at the full rate would
      // then hand the whole erz budget to one mouth and leave this step's own
      // cities with none. The same DEMAND_SUPPLY_RATIO every good gets, applied
      // to whichever of the three actually binds, keeps both true.
      const w = DEMAND_SUPPLY_RATIO * Math.min(
        refineries.length * REFINERY_RATE,
        budgets.get("holz")! / RECIPE.holz,
        budgets.get("erz")! / RECIPE.erz,
      );
      if (w > 0) {
        const town = fresh.reduce(
          (best, c) => (nearest(c, refineries) < nearest(best, refineries) ? c : best),
          fresh[0]!);
        town.demandMax.werkzeug = w;
        town.demand.werkzeug = w * DEMAND_START_FRACTION;
        budgets.set("holz", budgets.get("holz")! - w * RECIPE.holz);
        budgets.set("erz", budgets.get("erz")! - w * RECIPE.erz);
      }
    }

    // No production was added, so there is nothing to feed these mouths with.
    // Hand back the producers alone rather than a map that cannot be supplied.
    // Unreachable with the shipped configs; see the seed sweep.
    if (![...budgets.values()].some((b) => b > 0)) {
      return finish(added.filter((n) => n.kind !== "city"));
    }
    // One weight per city, not per good: how greedy a city is, is a property of
    // the city, and drawing per good would make DEMAND_SPREAD mean something
    // different for a two-good city than for its neighbour.
    const weights = fresh.map(() => 1 + r() * DEMAND_SPREAD);
    for (const g of GOOD_IDS_ALL) {
      const budget = budgets.get(g)!;
      const eaters = fresh.filter((c) => wanted.get(c)!.includes(g));
      if (budget <= 0 || eaters.length === 0) continue;
      const total = eaters.reduce((s, c) => s + weights[fresh.indexOf(c)]!, 0);
      for (const c of eaters) {
        c.demandMax[g] = (budget * weights[fresh.indexOf(c)]!) / total;
        c.demand[g] = c.demandMax[g] * DEMAND_START_FRACTION;
      }
    }
  }

  return finish(added);
}

/**
 * The nodes a milestone adds, generated from the world's own stream.
 *
 * `seed + stage` keeps a world reproducible from one number at any size, and
 * mulberry32 mixes its increment, so neighbouring stages do not walk together.
 */
export function extensionFor(s: GameState, id: MilestoneId): GameNode[] {
  const stage = MILESTONES.findIndex((m) => m.id === id) + 1;
  return generateNodes(s.seed + stage, EXTENSION_CONFIG[id], s.nodes);
}

export const regionRect = (index: number): WorldRect => index === 0 ? { x: 0, y: 0, width: WORLD_W, height: WORLD_H } : { x: WORLD_W + (index - 1) * REGION_W, y: 0, width: REGION_W, height: WORLD_H };
export const worldRect = (s: GameState): WorldRect => ({ x: 0, y: 0, width: s.regionCount === 1 ? WORLD_W : regionRect(s.regionCount - 1).x + REGION_W, height: WORLD_H });
export const regionForX = (x: number, regionCount: number): number =>
  x < WORLD_W ? 0 : Math.min(regionCount - 1, 1 + Math.floor((x - WORLD_W) / REGION_W));

export function localRegionPlan(s: GameState, nodes: readonly GameNode[]): [ProducerNode, CityNode][] | null {
  void s;
  const cities = nodes.filter((n): n is CityNode => n.kind === "city");
  const producers = nodes.filter((n): n is ProducerNode => n.kind === "producer");
  const plan = producers.map((producer) => {
    const targets = cities.filter((city) => city.demandMax[producer.good] > 0)
      .sort((a, b) => distance(producer, a) - distance(producer, b) || a.id - b.id);
    return targets[0] ? [producer, targets[0]] as [ProducerNode, CityNode] : null;
  });
  return plan.every((pair): pair is [ProducerNode, CityNode] => pair !== null) ? plan : null;
}
export function generateRegion(s: GameState): GameNode[] | null {
  if (s.regionCount >= MAX_REGIONS) return null;
  const config: WorldConfig = { ...EXTENSION_CONFIG["weites-land"], producers: 2, cities: 2, transfers: 0, bias: "wide", refines: false };
  const nodes = generateNodes((s.seed ^ Math.imul(s.regionCount, REGION_STREAM)) >>> 0, config, s.nodes, regionRect(s.regionCount));
  return nodes.length === 4 ? nodes : null;
}

/**
 * One producer of `good`, placed by the same rules generateNodes() uses
 * (RFC-0017 §3.4). Returns null when nobody wants the good, or when the board
 * has no room left for another node.
 *
 * **The player pays, the generator places.** `minDistance`, the two strips and
 * the drain cap are geometry this file owns, and each one is load-bearing for a
 * guard that already exists — a sawmill dropped 20 px from a city breaks the
 * click targets and `drainable()` in one gesture. So a commission is a
 * generation step with one producer in it, not a second way to make a node.
 *
 * The caller appends the node and allots the demand for it; this function
 * touches nothing. See buildProducer() in sim.ts, which is the only reason to
 * ever call it.
 */
export function commissionProducer(
  s: GameState, good: ProducedGoodId,
): ProducerNode | null {
  // Supply with no mouth lowers the demand-to-supply ratio and makes the
  // player's own cargo worth less (RFC-0017 §1.3). Refused here rather than in
  // the action, because the rate cap below has no city to measure against
  // either and would silently fall back to the whole list.
  const wanted = s.nodes.some((n) => n.kind === "city" && n.demandMax[good] > 0);
  if (!wanted) return null;

  const last = s.milestones[s.milestones.length - 1];
  const config: WorldConfig = {
    ...(last ? EXTENSION_CONFIG[last] : START_CONFIG),
    producers: 1,
    cities: 0,
    transfers: 0,
    // The brief of the stage this map last grew by is not this node's brief:
    // nothing is being taught here, so any valid spot will do.
    bias: "none",
    good,
  };
  // Deterministic, and distinct from every stage's stream: a milestone draws
  // from `seed + 1..MILESTONES.length`, and a map has more nodes than that from
  // the first day. Each commission adds one, so no two draw the same numbers.
  const added = generateNodes(s.seed + s.nodes.length, config, s.nodes, regionRect(s.regionCount - 1));
  return added.find((n): n is ProducerNode => n.kind === "producer") ?? null;
}

/**
 * What share of this good's production is already spoken for — the cities'
 * ceilings **plus what the recipe takes to make the werkzeug they want**.
 *
 * The claim, not the raw appetite, and that difference is the whole reason this
 * function exists. A refinery's werkzeug budget is paid for out of both input
 * budgets (RFC-0015 §1.2), so a map's holz cities hold about 0,73 of the holz
 * while the *claim* on it is the full `DEMAND_SUPPLY_RATIO`. Withdraw at 0,73
 * when a works closes and the recipe's share — which does not shrink with it —
 * grows against a smaller supply until the map is unsolvable for that good.
 *
 * Supply that arrives or leaves therefore moves demand at **this** ratio rather
 * than at the constant: §2 promises the ratio is never moved, only the scale of
 * both sides, and this is the ratio that has to hold still.
 *
 * Falls back to the constant when nothing produces `g` yet, which is the value a
 * first producer would establish anyway.
 */
export function demandRatio(s: GameState, g: ProducedGoodId): number {
  const supply = s.nodes.reduce((sum, n) =>
    sum + (n.kind === "producer" && n.good === g ? n.rate : 0), 0);
  if (supply <= 0) return DEMAND_SUPPLY_RATIO;
  const claim = s.nodes.reduce((sum, n) => sum + (n.kind === "city"
    ? n.demandMax[g] + n.demandMax.werkzeug * RECIPE[g]
    : 0), 0);
  return claim / supply;
}

/**
 * Moves `amount` of appetite for `g` across the cities that want it, split in
 * proportion to what they want today (RFC-0017 §3.2). Negative withdraws.
 *
 * Proportional rather than equal: the shape of demand across the map is
 * something the generator decided with `DEMAND_SPREAD`, and a purchase should
 * scale it, not flatten it. Deterministic, because `step()` may not draw.
 *
 * **Both directions, and the second one is not optional.** The invariant is an
 * equality, so a producer that closes has to take its mouths with it or the map
 * stands above its own supply the next morning (RFC-0017 §3.5). A withdrawal is
 * bounded by construction: it is `DEMAND_SUPPLY_RATIO × rate` of one producer
 * against `DEMAND_SUPPLY_RATIO × Σ rate` of all of them, so no city's ceiling
 * can be driven through zero.
 *
 * The running demand follows its ceiling down; the backlog does not. What a city
 * was owed stays owed, pays full price and still counts against its trust — the
 * same grace period a demand swap leaves behind (RFC-0016 §3.5).
 *
 * Returns what was actually moved — 0 when nobody wants `g`, which is the
 * caller's signal to refuse the purchase rather than raise supply alone.
 */
export function allotDemand(
  s: GameState, g: GoodId, amount: number, weightOf?: (c: CityNode) => number,
): number {
  const takers = s.nodes.filter((n): n is CityNode =>
    n.kind === "city" && n.demandMax[g] > 0);
  const weight = weightOf ?? ((c: CityNode) => c.demandMax[g]);
  let total = takers.reduce((sum, c) => sum + weight(c), 0);
  let by = weight;
  // A weighting nobody scores falls back to the ceiling itself, so the caller
  // never has to check whether its own rule happens to apply here.
  if (total <= 0) {
    by = (c: CityNode) => c.demandMax[g];
    total = takers.reduce((sum, c) => sum + by(c), 0);
  }
  if (takers.length === 0 || total <= 0 || amount === 0) return 0;
  for (const c of takers) {
    const share = (amount * by(c)) / total;
    c.demandMax[g] = Math.max(0, c.demandMax[g] + share);
    c.demand[g] = Math.min(c.demand[g], c.demandMax[g]);
  }
  return amount;
}

/** The seed is required. A default would silently ship one map to everybody. */
export function createGame(seed: number): GameState {
  return {
    seed,
    regionCount: 1,
    money: START_MONEY,
    day: 1,
    dayTime: 0,
    earned: 0,
    milestones: [],
    nodes: generateNodes(seed),
    routes: [],
    trucks: [],
    corridors: [],
    nextRouteId: 1,
    nextCorridorId: 1,
    nextTruckId: 1,
  };
}
